import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import { oncallJsonPath } from './paths';
import type { CurrentOncall, OncallResponse } from 'types/api-responses';

const CURRENT_ONCALL_QUERY_KEY = 'feedback-oncall';
const ONCALL_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * GET /feedback/oncall.json.
 *
 * @return The query options for this week's on-call engineer.
 */
const currentOncallQueryOptions = () =>
  queryOptions({
    queryKey: [CURRENT_ONCALL_QUERY_KEY],
    // The server holds the answer for CACHE_TTL (15 minutes), so asking again sooner than a
    // few minutes cannot learn anything. Without this, every mount of the feedback page sent
    // another request.
    staleTime: ONCALL_STALE_TIME_MS,
    queryFn: ({ signal }) =>
      api.get(oncallJsonPath(), { signal }).json<OncallResponse>(),
  });

/**
 * Who is on call this week, for the line at the top of the report form.
 *
 * Called by the form while it is open and by the feedback page, which share one query. A
 * rotation can hand over while the app stays open, so the answer is read again once it is a
 * few minutes old; the server caches it for longer, so that is not another trip to Jira.
 *
 * Any failure, or a payload missing a name or location, lands on 'unavailable'. The line is
 * a courtesy; nothing about submitting a report depends on it.
 *
 * @return {CurrentOncall} The lookup's state.
 */
export function useCurrentOncall(): CurrentOncall {
  const { data, isPending } = useQuery(currentOncallQueryOptions());

  // data before isError: a failed background refetch keeps the last good answer and sets
  // isError, and the answer from five minutes ago is still better than no line at all.
  if (data) {
    return data.state === 'ok' && data.name && data.geo && data.location
      ? data
      : { state: 'unavailable' };
  }
  if (isPending) return { state: 'loading' };

  return { state: 'unavailable' };
}
